// src/features/banquetOwnerPanel/pages/OwnerFillFormPage.jsx
//
// Public, no-login page at /owner-fill/:token — the "Generate Owner Link" flow from Super
// Admin Quick Add lands owners here to fill in everything Google couldn't supply (pricing,
// capacity, facilities, timing, etc.). Google-sourced fields render read-only and the
// photo/document upload sections are hidden (an anonymous visitor has no Storage upload
// path). The token is single-use — enforced server-side in the submitOwnerFillForm Cloud
// Function, not just by this page's UI.
//
// Pair: Super Admin's "Generate Owner Link" (GenerateOwnerLinkButton.jsx) and the
// generateOwnerFillLink / getBanquetByOwnerFillToken / submitOwnerFillForm Cloud Functions.

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FaCheck, FaCircleExclamation, FaPaperPlane } from "react-icons/fa6";
import { getBanquetByOwnerFillToken, submitOwnerFillForm } from "../services/banquetOwnerBanquetService.js";
import BanquetFormSections from "../components/BanquetFormSections.jsx";
import { buildBanquetFormPayload, computeMissingRequiredFields, emptyForm, formFromBanquet } from "../model/banquetFormModel.js";

const LINK_INVALID_MESSAGE = "This link is invalid.";
const LINK_USED_MESSAGE = "This link has already been used.";

export default function OwnerFillFormPage() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | invalidToken | alreadyUsed | error | ready | submitting | submitted
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [banquet, setBanquet] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    let mounted = true;
    getBanquetByOwnerFillToken({ token })
      .then((response) => {
        if (!mounted) return;
        setBanquet(response.banquet);
        setForm(formFromBanquet(response.banquet));
        setStatus("ready");
      })
      .catch((fetchError) => {
        if (!mounted) return;
        if (fetchError.message === LINK_USED_MESSAGE) setStatus("alreadyUsed");
        else if (fetchError.message === LINK_INVALID_MESSAGE) setStatus("invalidToken");
        else {
          setStatus("error");
          setLoadError(fetchError.message || "Unable to load this link. Please try again.");
        }
      });
    return () => {
      mounted = false;
    };
  }, [token]);

  const patch = (section, key, value) => setForm((current) => ({ ...current, [section]: { ...current[section], [key]: value } }));
  const patchForm = (updater) => setForm((current) => (typeof updater === "function" ? updater(current) : updater));
  const setTop = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const missingRequiredFields = useMemo(() => computeMissingRequiredFields(form), [form]);

  const handleSubmit = async () => {
    setSubmitError("");
    setStatus("submitting");
    try {
      const payload = buildBanquetFormPayload({
        form,
        selectedBanquet: banquet,
        owner: null,
        banquetId: banquet.id,
        previewSlug: banquet.slug || banquet.id,
      });
      await submitOwnerFillForm({ token, banquet: payload });
      setStatus("submitted");
    } catch (error) {
      if (error.message === LINK_USED_MESSAGE) {
        setStatus("alreadyUsed");
      } else {
        setStatus("ready");
        setSubmitError(error.message || "Unable to submit. Please try again.");
      }
    }
  };

  if (status === "loading") {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f5f7f4] p-4 text-slate-800">
        <p className="text-sm font-black">Loading your banquet details...</p>
      </div>
    );
  }

  if (status === "invalidToken" || status === "alreadyUsed" || status === "error") {
    const heading = status === "alreadyUsed" ? "This link has already been used." : status === "invalidToken" ? "This link is invalid." : "Something went wrong.";
    const detail =
      status === "alreadyUsed"
        ? "The details for this banquet have already been submitted. If you need to make changes, please contact DecorZoom."
        : status === "invalidToken"
        ? "This owner-fill link doesn't match any banquet awaiting details. Please check the link or contact DecorZoom for a new one."
        : loadError;
    return (
      <div className="grid min-h-screen place-items-center bg-[#f5f7f4] p-4">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <FaCircleExclamation className="mx-auto mb-3 text-3xl text-amber-500" />
          <h1 className="text-lg font-black text-slate-950">{heading}</h1>
          <p className="mt-2 text-sm text-slate-600">{detail}</p>
        </div>
      </div>
    );
  }

  if (status === "submitted") {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f5f7f4] p-4">
        <div className="w-full max-w-md rounded-3xl border border-emerald-200 bg-white p-6 text-center shadow-sm">
          <FaCheck className="mx-auto mb-3 text-3xl text-emerald-600" />
          <h1 className="text-lg font-black text-slate-950">Thank you!</h1>
          <p className="mt-2 text-sm text-slate-600">Your banquet details have been sent to DecorZoom for review. We'll get in touch once it's approved and live.</p>
        </div>
      </div>
    );
  }

  const isSubmitting = status === "submitting";

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-5 pb-32 text-slate-900 sm:px-5 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-5 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">DecorZoom</p>
          <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-950 sm:text-4xl">Complete your banquet listing</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            {banquet?.name ? <>We've already fetched <strong>{banquet.name}</strong>'s name, address, rating, and photos from Google — those fields are locked below.</> : "Some fields are locked below because we already fetched them from Google."} Fill in the rest so DecorZoom can review and publish your listing.
          </p>
        </header>

        {submitError ? <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700"><FaCircleExclamation className="mr-2 inline" />{submitError}</div> : null}

        <main className="space-y-5">
          <BanquetFormSections form={form} patch={patch} patchForm={patchForm} setTop={setTop} lockGoogleSourcedFields hideUploadSections />
        </main>
      </div>

      <footer className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-3 py-3 shadow-2xl backdrop-blur sm:px-5">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <p className={`truncate text-xs font-semibold ${missingRequiredFields.length ? "text-amber-700" : "text-emerald-700"}`}>
            {missingRequiredFields.length ? `Still empty: ${missingRequiredFields.slice(0, 5).map((item) => item.label).join(", ")}${missingRequiredFields.length > 5 ? "..." : ""} (you can submit and complete these later)` : "Required fields complete."}
          </p>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleSubmit}
            className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaPaperPlane /> {isSubmitting ? "Submitting..." : "Save & Send for Approval"}
          </button>
        </div>
      </footer>
    </div>
  );
}
